import { useDispatch } from "react-redux";

import DraftType from "@mapmycustomers/shared/enum/DraftType";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import { isDefined } from "@mapmycustomers/shared/util";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";

import EmailContext from "@app/component/email/creation/EmailContext";
import { createEmailDraft, updateEmailDraft } from "@app/store/email/actions";

const useEmailDraftSave = () => {
  const dispatch = useDispatch();

  const {
    attachedFiles,
    body,
    draftId,
    draftSaving,
    entities,
    entityType,
    onDraftCreated,
    onDraftSavingChange,
    resources,
    subject,
  } = useNonNullableContext(EmailContext);

  return useDynamicCallback(
    ({ onFailure, onSuccess }: { onFailure?: () => void; onSuccess?: () => void } = {}) => {
      if (draftSaving) {
        return;
      }

      onDraftSavingChange(true);

      const draftResources = Array.from(resources.values());
      const draftAttachments = attachedFiles.map(({ file }) => file).filter(isDefined);
      const savingDraft: Pick<EmailDraft, "content" | "title" | "type"> = {
        content: body,
        title: subject,
        type: DraftType.EMAIL,
      };

      const failureCallback = () => {
        onFailure?.();
      };

      const finishCallback = () => {
        onDraftSavingChange(false);
      };

      if (draftId) {
        dispatch(
          updateEmailDraft.request({
            attachments: draftAttachments,
            callback: () => {
              onDraftSavingChange(false);
              onSuccess?.();
            },
            draft: {
              id: draftId,
              ...savingDraft,
            },
            entities,
            failureCallback,
            finishCallback,
            resources: draftResources,
          })
        );
      } else {
        dispatch(
          createEmailDraft.request({
            attachments: draftAttachments,
            callback: (draft) => {
              onDraftCreated(draft.id);
              onDraftSavingChange(false);
              onSuccess?.();
            },
            draft: savingDraft,
            entities,
            entityType,
            failureCallback,
            finishCallback,
            resources: draftResources,
          })
        );
      }
    }
  );
};

export default useEmailDraftSave;
